import { createRoot } from "react-dom/client";
import App from "./App.tsx";
import "./index.css";

// One-shot auth reset: clear any stale/temporary tokens and force re-login.
// Bumping the version below re-runs this for all clients exactly once.
const AUTH_RESET_VERSION = "auth-reset-v6";
try {
  if (!localStorage.getItem(AUTH_RESET_VERSION)) {
    // Wipe all known auth + token cache keys
    const keysToClear = [
      "main-auth-token",
      "local-auth-token",
      "supabase.auth.token",
    ];
    keysToClear.forEach((k) => localStorage.removeItem(k));
    // Also nuke anything that looks like a Supabase auth/token entry
    Object.keys(localStorage).forEach((k) => {
      if (/auth-token|supabase|sb-/i.test(k)) localStorage.removeItem(k);
    });
    try {
      sessionStorage.clear();
    } catch {
      /* ignore */
    }
    localStorage.setItem(AUTH_RESET_VERSION, String(Date.now()));
    // Hard redirect to login with a clean slate
    window.location.replace("/auth");
  }
} catch {
  /* storage unavailable — continue */
}

// One-shot recovery for stale entry/preload chunks after a redeploy.
// Catches failures that happen before any React error boundary is mounted
// (initial bundle, vendor chunks, modulepreload), preventing a permanent
// white screen when a user returns to the tab after we've shipped a new build.
// Shares the `lazy-chunk-reloaded` sessionStorage guard with lazy-with-reload.ts
// so we never loop.
const CHUNK_RELOAD_FLAG = "lazy-chunk-reloaded";
const isChunkErr = (v: unknown) => {
  const m = v instanceof Error ? `${v.name} ${v.message}` : String(v ?? "");
  return /ChunkLoadError|Loading chunk [\d]+ failed|Failed to fetch dynamically imported module|Importing a module script failed|dynamically imported module/i.test(
    m,
  );
};
const tryReloadOnce = () => {
  try {
    if (sessionStorage.getItem(CHUNK_RELOAD_FLAG)) return;
    sessionStorage.setItem(CHUNK_RELOAD_FLAG, "1");
    window.location.reload();
  } catch {
    /* ignore */
  }
};
window.addEventListener("error", (e) => {
  if (isChunkErr(e.error) || isChunkErr(e.message)) tryReloadOnce();
});
window.addEventListener("unhandledrejection", (e) => {
  if (isChunkErr(e.reason)) tryReloadOnce();
});
window.addEventListener("vite:preloadError", () => tryReloadOnce());

createRoot(document.getElementById("root")!).render(<App />);
